import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, UserCog } from "lucide-react";
import { useState } from "react";
import { generatePath, useParams } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { SettingsPageLayout } from "components/settings/settings-page-layout";

import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as QueryKeys from "data";
import { OrgMembership, Role } from "data/models";
import { updateUserRoleOrgMembership } from "data/mutations-common";
import {
  fetchMemberUserDetails,
  fetchOrganisation,
  fetchUserOrgMembership,
} from "data/queries-common";
import * as Routes from "routes";
import { formatTimestamp } from "utils";

import { ResetTwoFactorCard, RoleSelector, UserCard } from "./cards";

const OrgMemberDetails = () => {
  const { accountId, orgId } = useParams<{
    accountId: string;
    orgId: string;
  }>();

  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] =
    useState(false);
  const [confirmationName, setConfirmationName] = useState("");

  const { enqueueSupportSnackbar, enqueueInfoSnackbar } = useCustomSnackbars();

  const userLink = generatePath(Routes.orgMembers);
  const queryClient = useQueryClient();

  const memberAccountDetailsQuery = useQuery({
    queryKey: [QueryKeys.organisationMemberAccount, accountId],
    queryFn: () => fetchMemberUserDetails(accountId),
    enabled: !!accountId,
  });

  const orgMembershipQuery = useQuery({
    queryKey: [QueryKeys.organisationMemberDetails, accountId],
    queryFn: () => fetchUserOrgMembership(accountId),
    enabled: !!accountId,
  });

  const organisationQuery = useQuery({
    queryKey: [QueryKeys.organisation],
    queryFn: () => fetchOrganisation(),
  });

  const updateOrgMembershipMutation = useMutation({
    mutationFn: ({ role }: { role: string }) =>
      updateUserRoleOrgMembership(role, accountId, orgId),
    onSuccess: (membership: OrgMembership) => {
      queryClient.setQueryData(
        [QueryKeys.organisationMemberDetails, accountId],
        membership,
      );
      queryClient.invalidateQueries({ queryKey: [QueryKeys.userMembership] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.permissions] });
    },
    onError: (error: Error) => {
      enqueueSupportSnackbar(`${error.toString()}`, "error");
    },
  });

  const transferOwnershipMutation = useMutation({
    mutationFn: () => updateUserRoleOrgMembership(Role.OWNER, accountId, orgId),
    onSuccess: () => {
      const orgName = organisationQuery.data?.name;
      const memberName =
        memberAccountDetailsQuery.data?.firstName &&
        memberAccountDetailsQuery.data?.lastName
          ? `${memberAccountDetailsQuery.data.firstName} ${memberAccountDetailsQuery.data.lastName}`
          : memberAccountDetailsQuery.data?.email;

      enqueueInfoSnackbar(
        `Ownership of ${orgName} has been successfully transferred to ${memberName}. Your role has been updated to Admin.`,
        "success",
      );

      setIsTransferOwnershipModalOpen(false);
      setConfirmationName("");

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationMemberDetails, accountId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationInvitations],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.permissions] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.userMembership] });
    },
    onError: (error: Error) => {
      enqueueSupportSnackbar(error.message, "error");
    },
  });

  const memberDisplayName =
    memberAccountDetailsQuery.data?.firstName &&
    memberAccountDetailsQuery.data?.lastName
      ? `${memberAccountDetailsQuery.data.firstName} ${memberAccountDetailsQuery.data.lastName}`
      : memberAccountDetailsQuery.data?.email || "this member";

  const orgName = organisationQuery.data?.name ?? "";
  const confirmationMatches = confirmationName.trim() === orgName.trim();

  const backButton = (
    <Button variant="default" asChild>
      <RouterLink to={userLink}>
        <ArrowLeft />
        Back
      </RouterLink>
    </Button>
  );

  return (
    <SettingsPageLayout
      pageTitle={{
        icon: UserCog,
        title: "Member Details",
        description: "Manage a member's role, access, and ownership.",
        actions: backButton,
      }}
    >
      <ReactQueryErrorWrapper
        queries={[
          memberAccountDetailsQuery,
          orgMembershipQuery,
          organisationQuery,
        ]}
      />

      <div className="mx-auto w-full max-w-3xl space-y-4">
        {memberAccountDetailsQuery.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : memberAccountDetailsQuery.isSuccess ? (
          <UserCard
            name={
              memberAccountDetailsQuery.data.firstName +
              " " +
              memberAccountDetailsQuery.data.lastName
            }
            email={memberAccountDetailsQuery.data.email}
            status={memberAccountDetailsQuery.data.status}
            added={formatTimestamp(memberAccountDetailsQuery.data.created)}
          />
        ) : null}

        <ResetTwoFactorCard />

        {orgMembershipQuery.data?.role !== Role.OWNER && (
          <PermissionsWrapper
            keys={[PermissionsKeys.membersChangeOwner]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
            }}
            showLoadingState={false}
          >
            <Card>
              <CardHeader>
                <CardTitle>Transfer Ownership</CardTitle>
                <CardDescription>
                  Permanently transfer ownership of this organisation to{" "}
                  {memberDisplayName}. This action cannot be undone.
                </CardDescription>
              </CardHeader>
              <CardFooter className="justify-end">
                <Button
                  onClick={() => setIsTransferOwnershipModalOpen(true)}
                  disabled={!accountId || !orgId}
                >
                  Transfer Ownership
                </Button>
              </CardFooter>
            </Card>
          </PermissionsWrapper>
        )}

        {orgMembershipQuery.isPending ? (
          <Skeleton className="h-96 w-full" />
        ) : orgMembershipQuery.data?.role ? (
          <RoleSelector
            memberRole={orgMembershipQuery.data.role}
            onSubmit={updateOrgMembershipMutation}
          />
        ) : null}
      </div>

      <Dialog
        open={isTransferOwnershipModalOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setIsTransferOwnershipModalOpen(false);
            setConfirmationName("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Ownership</DialogTitle>
            <DialogDescription>
              This is a permanent and irreversible action.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <p>
              {memberDisplayName} will have <b>full, unrestricted control</b>{" "}
              over the {orgName} organisation.
            </p>
            <p>
              Upon successful transfer your role will be immediately changed to{" "}
              <b>admin</b>.
            </p>
            <p>
              To confirm, please type your organisation's name <b>{orgName}</b>.
            </p>
            <Field>
              <FieldLabel htmlFor="confirm-org-name">
                Type &quot;{orgName}&quot; to confirm
              </FieldLabel>
              <Input
                id="confirm-org-name"
                value={confirmationName}
                onChange={(e) => setConfirmationName(e.target.value)}
                placeholder="Organisation name"
              />
            </Field>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsTransferOwnershipModalOpen(false);
                setConfirmationName("");
              }}
              disabled={transferOwnershipMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                !confirmationMatches || transferOwnershipMutation.isPending
              }
              onClick={() => transferOwnershipMutation.mutate()}
            >
              {transferOwnershipMutation.isPending ? (
                <>
                  <Spinner />
                  Transfer Ownership
                </>
              ) : (
                "Transfer Ownership"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPageLayout>
  );
};

export default OrgMemberDetails;
